// Best-effort client-side error reporting. Captures uncaught errors and
// unhandled promise rejections and forwards them to /api/admin/logs/client so
// they show up on the /logs page next to the server logs.
//
// Deliberately uses a RAW fetch (not the api() helper): a failing report must
// never throw or recurse into more reports. Identical messages are deduped for
// a short window so an error in a render loop can't flood the buffer.

import { getToken } from "../api";

const recent = new Map<string, number>();
const DEDUPE_MS = 10_000;

export function reportClientError(
  message: string,
  source: string = typeof location !== "undefined" ? location.pathname : "",
  stack = "",
  level: "error" | "warn" = "error",
): void {
  try {
    if (!message) return;
    const key = `${level}:${message}:${source}`;
    const now = Date.now();
    const last = recent.get(key);
    if (last && now - last < DEDUPE_MS) return;
    if (recent.size > 200) recent.clear();
    recent.set(key, now);

    const token = getToken();
    if (!token) return; // not authenticated — nothing to report to

    void fetch("/api/admin/logs/client", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        message: message.slice(0, 2000),
        source: source.slice(0, 500),
        stack: stack.slice(0, 4000),
        level,
      }),
      keepalive: true,
    }).catch(() => { /* reporting is best-effort */ });
  } catch { /* must never throw */ }
}

let installed = false;

export function installErrorLogging(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (e: ErrorEvent) => {
    const msg = e.message || String((e.error && e.error.message) || "Unknown error");
    const where = e.filename
      ? `${location.pathname} · ${e.filename}:${e.lineno}:${e.colno}`
      : location.pathname;
    reportClientError(msg, where, (e.error && e.error.stack) || "");
  });

  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const r = e.reason;
    const msg = r instanceof Error ? r.message : String(r);
    reportClientError(`Unhandled rejection: ${msg}`, location.pathname, r instanceof Error ? (r.stack ?? "") : "");
  });
}
